import { LISTING_STATUS } from '../../../../store/products/constants/listingStatus';
import {
  getListingProductStatusFromMap,
  hasListingProductInMap,
  hasLiveInProductListingsMap,
} from '../../../../store/selectors/productSelectors';
import SYNC_FLOW_MEDIA_STEPS from '../constants/syncFlowMediaSteps';
import {
  getProductMediaAssets,
  getVariantMediaAssets,
} from './syncFlowMediaHelpers';

export const CHANNEL_SYNC_ACTION = {
  DRAFT: 'draft',
  PUBLISH: 'publish',
};

export const createInitialFlowState = ({
  productId,
  masterVariantIds,
  isVariantSync,
}) => ({
  productId,
  masterVariantIds,
  isVariantSync,
  selectedChannels: [],
  channelActionsByChannelId: {},
  overrideExisting: true,
  publishingScope: { text: true, media: false },
  productMediaByChannel: {},
  variantMediaByChannel: {},
  variantMediaStepCompleted: false,
});

export const hasListingForChannel = (productListings, channelId) => (
  hasListingProductInMap(productListings, channelId)
);

export const hasListingForAllChannels = (selectedChannels, productListings) => (
  selectedChannels.every((channelId) => hasListingForChannel(productListings, channelId))
);

export const shouldSkipMediaSteps = (scope) => !scope?.media;

export const getMediaSteps = ({
  product,
  isVariantSync,
  scope,
}) => {
  if (shouldSkipMediaSteps(scope)) {
    return [];
  }

  if (isVariantSync) {
    return [SYNC_FLOW_MEDIA_STEPS.VARIANT_MEDIA];
  }

  const hasVariants = Boolean(product?.variants?.length);

  if (hasVariants) {
    return [SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA, SYNC_FLOW_MEDIA_STEPS.VARIANT_MEDIA];
  }

  return [SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA];
};

export const filterMediaStepsByAvailableAssets = ({
  steps,
  product,
  masterVariantIds,
  productsState,
}) => steps.filter((step) => {
  if (step === SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA) {
    return getProductMediaAssets(product, productsState).length > 0;
  }

  if (step === SYNC_FLOW_MEDIA_STEPS.VARIANT_MEDIA) {
    return getVariantMediaAssets(product, masterVariantIds, productsState).length > 0;
  }

  return true;
});

export const hasAttachedMediaForSync = ({
  product,
  isVariantSync,
  masterVariantIds,
  productsState,
}) => {
  const steps = filterMediaStepsByAvailableAssets({
    steps: getMediaSteps({ product, isVariantSync, scope: { text: true, media: true } }),
    product,
    masterVariantIds,
    productsState,
  });

  return steps.length > 0;
};

export const isMediaScopeAvailable = ({
  product,
  isVariantSync,
  masterVariantIds,
  productsState,
}) => (
  hasAttachedMediaForSync({ product, isVariantSync, masterVariantIds, productsState })
);

export const getDefaultPublishingScope = ({
  product,
  isVariantSync,
  masterVariantIds,
  productsState,
}) => {
  const mediaAvailable = isMediaScopeAvailable({
    product,
    isVariantSync,
    masterVariantIds,
    productsState,
  });

  return {
    text: true,
    media: mediaAvailable,
  };
};

export const mergeFlowState = (flowState, patch) => ({
  ...flowState,
  ...patch,
});

export const getDefaultChannelAction = (channelId, productListings) => {
  if (hasLiveInProductListingsMap(productListings, channelId)) {
    return CHANNEL_SYNC_ACTION.PUBLISH;
  }

  if (getListingProductStatusFromMap(productListings, channelId) === LISTING_STATUS.DRAFT) {
    return CHANNEL_SYNC_ACTION.DRAFT;
  }

  return CHANNEL_SYNC_ACTION.DRAFT;
};

export const buildInitialChannelActions = (channelOptions, productListings) => (
  channelOptions.reduce((acc, { value: channelId }) => ({
    ...acc,
    [channelId]: getDefaultChannelAction(channelId, productListings),
  }), {})
);

export const splitChannelsByAction = (channelActionsByChannelId) => {
  const draftChannels = [];
  const publishChannels = [];

  Object.entries(channelActionsByChannelId).forEach(([channelId, action]) => {
    if (action === CHANNEL_SYNC_ACTION.PUBLISH) {
      publishChannels.push(channelId);
    } else {
      draftChannels.push(channelId);
    }
  });

  return { draftChannels, publishChannels };
};

export const resolveSyncChannelIds = ({
  selectedChannelIds,
  conflictingChannelIds,
  overrideExisting,
}) => {
  if (overrideExisting) {
    return selectedChannelIds;
  }

  return selectedChannelIds.filter((channelId) => !conflictingChannelIds.includes(channelId));
};
